/**
 * Captures screenshots and video recordings from Appium sessions.
 *
 * Video recording uses a 3-tier fallback strategy:
 *   1. mobile: startMediaProjectionRecording (modern UiAutomator2)
 *   2. mobile: startScreenRecording (older Appium drivers)
 *   3. adb shell screenrecord (emulator fallback, requires deviceSerial)
 */

import { mkdirSync, writeFileSync } from 'node:fs';
import { join } from 'node:path';
import { spawn, execFileSync, type ChildProcess } from 'node:child_process';

export interface ScreenshotCollectorOptions {
  readonly enabled: boolean;
  readonly screenshotOnEvery: 'test' | 'failure' | 'never';
  readonly includeVideoRecording: boolean;
  readonly outputDir: string;
  /** ADB device serial (e.g. 'emulator-5554') — enables ADB screenrecord fallback */
  readonly deviceSerial?: string;
}

type DriverLike = {
  saveScreenshot(filepath: string): Promise<void>;
  // WebdriverIO's execute() is variadic: extra args are spread into the W3C "args" array.
  // To call `mobile: startMediaProjectionRecording` with an options object, pass the object
  // directly (not wrapped in an array): driver.execute(cmd, {}) → args:[{}] in the payload.
  execute(script: string, ...args: unknown[]): Promise<unknown>;
};

const ADB_RECORD_PATH = '/sdcard/testrelic-video.mp4';

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export class ScreenshotCollector {
  private readonly options: ScreenshotCollectorOptions;
  private readonly paths: string[] = [];
  private videoPath: string | null = null;
  private recording = false;
  private recordingViaAdb = false;
  private adbRecordProcess: ChildProcess | null = null;
  private stepCounter = 0;

  constructor(options: ScreenshotCollectorOptions) {
    this.options = options;
    if (this.options.enabled || this.options.includeVideoRecording) {
      mkdirSync(this.options.outputDir, { recursive: true });
    }
  }

  async captureScreenshot(driver: DriverLike, label: string): Promise<string | null> {
    if (!this.options.enabled || this.options.screenshotOnEvery === 'never') return null;

    this.stepCounter++;
    const filename = `${label}-${String(this.stepCounter).padStart(3, '0')}.png`;
    const filepath = join(this.options.outputDir, filename);

    try {
      await driver.saveScreenshot(filepath);
      this.paths.push(filepath);
      return filepath;
    } catch {
      return null;
    }
  }

  async captureOnFailure(driver: DriverLike, testTitle: string): Promise<string | null> {
    if (!this.options.enabled) return null;
    if (this.options.screenshotOnEvery === 'never') return null;

    const sanitized = testTitle.replace(/[^a-zA-Z0-9_-]/g, '_').substring(0, 80);
    return this.captureScreenshot(driver, `failure-${sanitized}`);
  }

  async startVideoRecording(driver: DriverLike): Promise<void> {
    if (!this.options.includeVideoRecording || this.recording) return;

    // Tier 1 & 2: Appium mobile commands (real devices / modern emulators with permissions)
    // Pass {} as the options object directly (variadic spread → args:[{}] in the W3C payload).
    for (const cmd of ['mobile: startMediaProjectionRecording', 'mobile: startScreenRecording']) {
      try {
        await driver.execute(cmd, {});
        this.recording = true;
        this.recordingViaAdb = false;
        return;
      } catch {
        // Try the next command
      }
    }

    // Tier 3: ADB screenrecord (emulator fallback — no permission dialog needed)
    const serial = this.options.deviceSerial;
    if (serial) {
      try {
        // Remove any stale recording from a previous test
        this._adbExec(serial, ['shell', 'rm', '-f', ADB_RECORD_PATH]);

        this.adbRecordProcess = spawn('adb', [
          '-s', serial,
          'shell', 'screenrecord',
          '--bit-rate', '4000000',
          '--size', '720x1280',
          ADB_RECORD_PATH,
        ], { stdio: 'ignore' });

        // Give screenrecord a moment to start writing
        await sleep(300);

        // If the process died immediately, it's not supported
        if (this.adbRecordProcess.exitCode !== null) {
          this.adbRecordProcess = null;
          return;
        }

        this.recording = true;
        this.recordingViaAdb = true;
      } catch {
        this.adbRecordProcess = null;
      }
    }
  }

  async stopVideoRecording(driver: DriverLike, testTitle: string): Promise<string | null> {
    if (!this.recording) return null;

    const sanitized = testTitle.replace(/[^a-zA-Z0-9_-]/g, '_').substring(0, 80);
    const filepath = join(this.options.outputDir, `video-${sanitized}.mp4`);

    // ADB screenrecord path
    if (this.recordingViaAdb && this.adbRecordProcess) {
      const serial = this.options.deviceSerial!;
      try {
        // SIGINT tells screenrecord to finalize the MP4 cleanly
        this.adbRecordProcess.kill('SIGINT');
        // Allow up to 3 s for the device to flush and finalize the file
        await sleep(3000);
        this._adbPull(serial, ADB_RECORD_PATH, filepath);
        this.videoPath = filepath;
        return filepath;
      } catch {
        // Recording pull failed — silently skip
        return null;
      } finally {
        this.adbRecordProcess = null;
        this.recording = false;
        this.recordingViaAdb = false;
      }
    }

    // Appium mobile command path
    for (const cmd of ['mobile: stopMediaProjectionRecording', 'mobile: stopScreenRecording']) {
      try {
        const base64 = await driver.execute(cmd, {});
        this.recording = false;

        if (typeof base64 === 'string' && base64.length > 0) {
          writeFileSync(filepath, Buffer.from(base64, 'base64'));
          this.videoPath = filepath;
          return filepath;
        }
      } catch {
        // Try the next command
      }
    }

    this.recording = false;
    return null;
  }

  getScreenshotPaths(): string[] {
    return [...this.paths];
  }

  getVideoPath(): string | null {
    return this.videoPath;
  }

  clear(): void {
    this.paths.length = 0;
    this.videoPath = null;
    this.stepCounter = 0;
    // Note: do NOT reset adbRecordProcess here — it may still be running between
    // beforeTest and afterTest. clear() is called at beforeTest start.
  }

  dispose(): void {
    if (this.adbRecordProcess) {
      try { this.adbRecordProcess.kill(); } catch { /* ignore */ }
      this.adbRecordProcess = null;
    }
    this.clear();
  }

  // ---------------------------------------------------------------------------
  // ADB helpers
  // ---------------------------------------------------------------------------

  private _adbExec(serial: string, args: string[]): void {
    execFileSync('adb', ['-s', serial, ...args], { stdio: 'ignore', timeout: 10_000 });
  }

  private _adbPull(serial: string, remote: string, local: string): void {
    execFileSync('adb', ['-s', serial, 'pull', remote, local], { stdio: 'ignore', timeout: 30_000 });
  }
}
